// data.js — Command Palette item sources (Actions + Segments + Wheels).
// WL (Fable Hail Mary Wave 5).
//
// An "item" is the palette's single unit. It is intentionally SERIALIZABLE
// (label + path + display fields only, no functions) so the exact object can be
// snapshotted into the recents store and re-rendered later without a refetch.
// Navigation is performed by the component (navigate(item.path)) — never a
// closure baked onto the item.
//
//   { id, kind:'action'|'segment'|'wheel', label, sublabel?, keywords?, path,
//     iconKey?  (action icon), portrait?  (wheel image), wheelName? }

import { useQuery } from "react-query";
import API from "../../services/request";
import { htmlToText } from "../../utils/helpers";
import { buildSegmentDeepLinkPath } from "../../utils/segmentDeepLink";
import { shouldRetryQuery } from "../../services/retryPolicy";

// ── Actions ──────────────────────────────────────────────────────────────────
// Static navigation targets. Keywords are hidden synonyms so "payment" finds
// Billing, "media"/"files" finds Storage, etc. Paths reuse the app's real routes
// (see src/routes.js + utils/config PATH) — the palette adds NO new routes.
export function buildActions(userId) {
  const galleryPath = userId ? `/gallery/${userId}` : "/gallery";
  return [
    {
      id: "action:create-wheel",
      kind: "action",
      iconKey: "create-wheel",
      label: "Create Wheel",
      sublabel: "New",
      keywords: ["new", "add", "make", "build", "wizard"],
      // Opens the real Create-a-Wheel wizard (CreateWheelPopup). The popup is
      // mounted on the Gallery page, so navigate there with a `?create=1` intent
      // that Gallery reads to open the wizard on arrival (see Gallery/index.jsx).
      // Not a new route — Gallery is a real route; the query param is the intent.
      path: `${galleryPath}?create=1`,
    },
    {
      id: "action:explore",
      kind: "action",
      iconKey: "explore",
      // Gordon, 2026-08-06: "the page is called Discover, not Explore." User-
      // facing label + sublabel renamed. Path renamed /explore -> /discover
      // (ISP-9999, Gordon 2026-08-09 "Full rename incl. URL"); id/iconKey stay
      // internal. Old "explore" keyword kept so a search for it still finds this.
      label: "Discover",
      sublabel: "Browse",
      keywords: ["explore", "discover", "browse", "feed", "trending"],
      path: "/discover",
    },
    {
      id: "action:gallery",
      kind: "action",
      iconKey: "gallery",
      label: "My Gallery",
      sublabel: "Your wheels",
      keywords: ["my wheels", "profile", "collection", "home"],
      path: galleryPath,
    },
    {
      id: "action:settings",
      kind: "action",
      iconKey: "settings",
      label: "Settings",
      sublabel: "Account",
      keywords: ["account", "preferences", "password", "visibility"],
      path: "/settings",
    },
    {
      id: "action:storage",
      kind: "action",
      iconKey: "storage",
      label: "Storage",
      sublabel: "Media library",
      keywords: ["media", "files", "images", "uploads", "space"],
      path: "/media-library",
    },
    {
      id: "action:billing",
      kind: "action",
      iconKey: "billing",
      label: "Subscription & Billing",
      sublabel: "Plan",
      keywords: ["billing", "payment", "plan", "invoice", "subscription", "upgrade"],
      path: "/settings?tab=subscriptions",
    },
    {
      id: "action:help",
      kind: "action",
      iconKey: "help",
      label: "Help & Support",
      sublabel: "FAQ",
      keywords: ["support", "faq", "contact", "docs"],
      path: "/help",
    },
  ];
}

// ── Segments of the currently-open wheel ─────────────────────────────────────
export function buildSegments(segments, wheelData) {
  if (!Array.isArray(segments) || segments.length === 0) return [];
  const wheelName = wheelData?.wheel_name
    ? htmlToText(wheelData.wheel_name)
    : "this wheel";
  const wheelId = wheelData?.id;
  return segments
    .filter((s) => s && s.id != null)
    .map((s) => ({
      id: `segment:${s.id}`,
      kind: "segment",
      label: (s.title && htmlToText(s.title)) || "Untitled segment",
      sublabel: `Segment · ${wheelName}`,
      // D-19: open the FULL ViewWheel with this segment opened, never the bare
      // /wheel/segment/:id embed route.
      path: buildSegmentDeepLinkPath(wheelId, s.id),
    }));
}

// ── Wheels (server-side search, debounced by the caller) ─────────────────────
// Maps one wheel result → item. Portrait uses `wheel_image` (AnalyticsWheelThumb
// prop). `wheelId` is the raw id (the row's `id` is the composite "wheel:<id>"
// string), kept separate so the row's thumbnail can fetch this wheel's own
// segments — same shape WheelTable.jsx passes to AnalyticsWheelThumb.
export function wheelToItem(w) {
  const name = w?.wheel_name ? htmlToText(w.wheel_name) : "Untitled wheel";
  return {
    id: `wheel:${w.id}`,
    kind: "wheel",
    label: name,
    sublabel: "Wheel",
    path: `/wheel/${w.id}`,
    portrait: w?.wheel_image || undefined,
    wheelName: name,
    wheelId: w.id,
  };
}

// Cap kept modest: a palette shows the best matches, not a full catalogue. The
// list is still virtualized (react-window) so even this many rows are cheap.
export const WHEEL_RESULT_CAP = 25;

/**
 * useWheelSearch — debounced-query wheel search over the EXISTING wheel-list
 * endpoint (same URL shape as services/queries.js `useInfiniteWheels`). No new
 * endpoint; single capped page; react-query caches by query string.
 *
 * @param {string} query   already-debounced search text ("" = disabled)
 * @param {string|number} userId
 * @param {boolean} enabled outer gate (e.g. palette open)
 */
export function useWheelSearch(query, userId, enabled = true) {
  const trimmed = (query || "").trim();
  const active = enabled && !!userId && trimmed.length > 0;

  const result = useQuery(
    ["cmdPalette.wheelSearch", userId, trimmed],
    async () => {
      const url =
        `/api/v2/wheel/?mode=created&user_id=${userId}&add_primary=true` +
        `&search=${encodeURIComponent(trimmed)}` +
        `&page_size=${WHEEL_RESULT_CAP}&page=1`;
      const response = await API.get(url);
      const data = response?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      return {
        items: results.slice(0, WHEEL_RESULT_CAP).map(wheelToItem),
        count: typeof data.count === "number" ? data.count : results.length,
      };
    },
    {
      enabled: active,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: shouldRetryQuery,
    }
  );

  return {
    ...result,
    // Only treat as loading when a search is actually in flight for this query.
    isSearching: active && (result.isLoading || result.isFetching),
    active,
  };
}
